import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'wouter'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Plus, Search, Eye } from 'lucide-react'
import { api } from '@/lib/api'
import { handleApiError } from '@/lib/api-error'
import type { AdminEmpresa, Paginated } from '@/types/admin'

const ESTADO_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  activa: 'default',
  pendiente: 'secondary',
  suspendida: 'destructive',
  cancelada: 'outline',
}

function useDebounced<T>(value: T, ms = 350): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

export function EmpresasListPage() {
  const [, navigate] = useLocation()
  const [q, setQ] = useState('')
  const [estado, setEstado] = useState<string>('all')
  const [page, setPage] = useState(1)
  const [perPage] = useState(20)
  const [data, setData] = useState<Paginated<AdminEmpresa> | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const debouncedQ = useDebounced(q)

  const queryString = useMemo(() => {
    const params = new URLSearchParams()
    if (debouncedQ) params.set('q', debouncedQ)
    if (estado && estado !== 'all') params.set('estado', estado)
    params.set('page', String(page))
    params.set('per_page', String(perPage))
    return params.toString()
  }, [debouncedQ, estado, page, perPage])

  useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoading(true)
      try {
        const res = await api.get<Paginated<AdminEmpresa>>(`/admin/empresas?${queryString}`)
        if (!cancelled) setData(res)
      } catch (err) {
        if (!cancelled) handleApiError(err, 'No se pudo cargar el listado de empresas.')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [queryString])

  useEffect(() => {
    setPage(1)
  }, [debouncedQ, estado])

  const empresas = data?.data ?? []
  const meta = data?.meta

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Empresas</h1>
          <p className="text-muted-foreground mt-1">Gestión de empresas suscritas a la plataforma</p>
        </div>
        <Button asChild>
          <Link href="/empresas/nueva">
            <Plus className="mr-2 h-4 w-4" /> Nueva empresa
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre, RUT o email…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={estado} onValueChange={setEstado}>
              <SelectTrigger className="sm:w-56">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="activa">Activa</SelectItem>
                <SelectItem value="pendiente">Pendiente</SelectItem>
                <SelectItem value="suspendida">Suspendida</SelectItem>
                <SelectItem value="cancelada">Cancelada</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>RUT</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Plan actual</TableHead>
                  <TableHead>Cupos</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      Cargando…
                    </TableCell>
                  </TableRow>
                ) : empresas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No hay empresas que coincidan con el filtro.
                    </TableCell>
                  </TableRow>
                ) : (
                  empresas.map((e) => {
                    const sus = e.suscripciones?.[0]
                    const totales = sus?.cantidad_cupos ?? 0
                    const planNombre = sus?.plan?.nombre ?? '—'
                    return (
                      <TableRow key={e.id}>
                        <TableCell className="font-medium">{e.nombre_fantasia}</TableCell>
                        <TableCell className="font-mono text-sm">{e.rut}</TableCell>
                        <TableCell>
                          <Badge variant={ESTADO_VARIANT[e.estado] ?? 'outline'}>{e.estado}</Badge>
                        </TableCell>
                        <TableCell>{planNombre}</TableCell>
                        <TableCell>
                          <span className="font-mono text-sm">
                            {totales}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/empresas/${e.id}`)}
                          >
                            <Eye className="mr-2 h-4 w-4" /> Ver detalle
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {meta && meta.last_page > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                Mostrando {meta.from ?? 0}–{meta.to ?? 0} de {meta.total}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <span>
                  Página {meta.current_page} de {meta.last_page}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= meta.last_page}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
